import type { QueueStep, QueueViewId, StockAdminQueue } from '@myrewards/contracts';
import { STAGE_WORDS } from '../../awards/present';
import type { HeroStat } from '../../shell';
import { daysBetween, formatCount, organizationDate, plural, type StatusTone } from '../../ui';
import { QUEUE_VIEW, STEP, WORK_HREF } from '../labels';
import { spotWorkHref } from '../../manage/pages';

type Row = StockAdminQueue['rows'][number];
type Person = StockAdminQueue['teammates'][number];

export const STEPS: readonly QueueStep[] = [
  'waiting_for_claim',
  'checking',
  'letters',
  'employee_choice',
  'past_deadline',
  'with_payroll',
  'statement_check',
  'shareworks',
];

const STEP_SLUG: Record<QueueStep, string> = {
  waiting_for_claim: 'waiting-for-a-claim',
  checking: 'checking',
  letters: 'letters',
  employee_choice: 'employee-choice',
  past_deadline: 'past-deadline',
  with_payroll: 'with-payroll',
  statement_check: 'statement-check',
  shareworks: 'shareworks',
};

/** `?step=checking` → the step, or null for anything else. */
export function stepFromParam(value: string | null): QueueStep | null {
  return STEPS.find((s) => STEP_SLUG[s] === value) ?? null;
}

/** A step tile filters "Everything in progress" to that step. */
export function stepHref(step: QueueStep): string {
  return `${QUEUE_VIEW.in_progress.href}?step=${STEP_SLUG[step]}`;
}

/** Rows in the view, narrowed to one step when a tile is chosen. Oldest first, as sent. */
export function visibleRows(queue: StockAdminQueue, step: QueueStep | null): Row[] {
  return step === null ? queue.rows : queue.rows.filter((row) => row.step === step);
}

/**
 * Whole days it has waited with the team, in the organization's calendar (the one its dates are
 * shown in), to `asOf`. Never below zero. An award sent at 6 pm Pacific on Oct 13 has waited a
 * day on Oct 14, as its date says, though it was already Oct 14 in UTC (APP-149 D1).
 */
export function ageDays(since: string, asOf: string): number {
  return Math.max(0, daysBetween(organizationDate(since), asOf));
}

/** "Today", "1 day", "3 days": how long it has waited with the team. */
export function age(since: string, asOf: string): string {
  const days = ageDays(since, asOf);
  return days === 0 ? 'Today' : plural(days, 'day', 'days');
}

/** "Age 5 days", "Age today": the Age value, under the Submitted date on Home (APP-149 D1). */
export function ageWords(since: string, asOf: string): string {
  const days = ageDays(since, asOf);
  return days === 0 ? 'Age today' : `Age ${plural(days, 'day', 'days')}`;
}

/** One award's letters (SA3). */
export function lettersHref(awardId: string): string {
  return `/queue/${awardId}/letters`;
}

/** A claim matters at the check and at letters; later steps have no claim to show. */
export function claimApplies(row: Row): boolean {
  return row.step === 'waiting_for_claim' || row.step === 'checking' || row.step === 'letters';
}

/**
 * Where an award opens from the queue: its letters at the letters step, its check while the team
 * has it, the past-deadline list with it open, the Shareworks worklist when it's there, else its
 * record.
 */
export function rowHref(row: Row): string {
  // APP-244: a Spot letter to review opens on Spot's own letter page, with its claim and title.
  if (row.type === 'SPOT') return spotWorkHref('fulfillment', { award: row.award.id });
  if (row.step === 'letters') return lettersHref(row.award.id);
  if (claimApplies(row)) return `/queue/${row.award.id}`;
  if (row.step === 'past_deadline')
    return `${QUEUE_VIEW.past_deadline.href}?award=${encodeURIComponent(row.award.id)}`;
  if (row.step === 'shareworks') return WORK_HREF.shareworks;
  if (row.step === 'statement_check') return WORK_HREF.earning_statements;
  return `/awards/${row.award.id}`;
}

/** Who an award at Approval waits on, the way SL2 says it. */
const WAITING_ON: Record<NonNullable<Row['approval']>['on'], string> = {
  sector_lead: 'Sector Lead',
  named_executive: 'Executive',
  executive: 'Executive decision',
  executives_outside_app: 'decided outside the app',
};

/**
 * The Stage column: the Stock Admin's step while the team has it, else the plain stage (QA-013),
 * with who an approval waits on: "Approval · Executive decision", "Approval · over budget".
 */
export function stepLabel(row: Row): string {
  if (row.type === 'SPOT' && row.step === 'letters') return 'Spot letter to review';
  if (row.step !== null) return STEP[row.step].label;
  const stage = STAGE_WORDS[row.stage ?? 'draft'].label;
  if (row.stage !== 'approval' || row.approval === null) return stage;
  return `${stage} · ${row.approval.overBudget ? 'over budget' : WAITING_ON[row.approval.on]}`;
}

/** The pill's tone: the step's, else the plain stage's. */
export function stepTone(row: Row): StatusTone {
  return row.step !== null ? STEP[row.step].tone : STAGE_WORDS[row.stage ?? 'draft'].tone;
}

/**
 * APP-130: at Letters, how many letters are out, and when a letter some of them waited on is ready
 * now (D16): "1 of 3 released · Missing letter ready". Null when there's nothing to say.
 */
export function lettersWords(row: Row): string | null {
  const l = row.letters;
  if (l == null) return null;
  const parts = [
    ...(l.released > 0 ? [`${String(l.released)} of ${String(l.total)} released`] : []),
    ...(l.templateArrived ? ['Missing letter ready: make the rest'] : []),
  ];
  return parts.length === 0 ? null : parts.join(' · ');
}

/** APP-244: why MyRewards couldn't make an award's letters, as a queue row says it briefly. */
const NOT_MADE: Readonly<Record<string, string>> = {
  award_date_required: 'no award date',
  tax_estimates_missing: 'tax to enter',
  letter_converter_unavailable: 'PDF service unavailable',
  letter_conversion_failed: 'a letter couldn’t become a PDF',
  letter_mapping_missing: 'no letter set up',
  employee_name_missing: 'no name in the directory',
  signer_required: 'no signer',
  signer_inactive: 'signer no longer active',
  citation_required: 'no letter wording',
  letter_doubled_word: 'a word printed twice',
  award_held: 'the award is on hold',
};

/**
 * APP-244: at Letters, MyRewards' own run of them: being made, or not made and briefly why, so
 * the row says where to look. Null once they're made, or when nothing speaks for them.
 */
export function automaticLettersWords(row: Row): { words: string; problem: boolean } | null {
  const a = row.letters?.automatic;
  if (a == null) return null;
  if (a.status === 'waiting') return { words: 'MyRewards is making the letters', problem: false };
  const reasons = [
    ...new Set(a.codes.map((code) => NOT_MADE[code] ?? 'open the letters to see why')),
  ];
  const [first, second] = reasons;
  const why =
    first === undefined
      ? 'open the letters to see why'
      : reasons.length <= 2
        ? [first, second].filter((r): r is string => r !== undefined).join(' and ')
        : `${first}, ${second ?? ''} and more`;
  return { words: `Letters not made: ${why}`, problem: true };
}

/** "Waiting on Graham Whitlock", for a delegate's entry at Approval. */
export function waitingOnWords(row: Row): string | null {
  const a = row.approval;
  if (row.step !== null || a === null) return null;
  if (a.on === 'sector_lead')
    return a.person === null ? 'Waiting on the Sector Lead' : `Waiting on ${a.person.name}`;
  if (a.on === 'named_executive')
    return a.person === null ? 'Waiting on the Executive' : `Waiting on ${a.person.name}`;
  if (a.on === 'executives_outside_app') return 'The Executives decide outside the app';
  return 'Any one Executive decides';
}

/**
 * Whether any award has ever reached the Stock Admin team: `true` once one has, `false` on day
 * one, `null` when it can't be told (the read failed or hasn't answered). Tells "all caught up"
 * from a first day (DESIGN-SPEC §5, QA-018, QA-030).
 */
export type QueueHistory = boolean | null;

/**
 * Under "My queue" in the hero (QA-016): what you hold, else what's waiting for anyone to claim.
 * "All caught up" only when nothing is waiting and the queue has had work (QA-030).
 */
export function myQueueMeta(queue: StockAdminQueue, history: QueueHistory): string {
  const { checks, letters } = queue.mine;
  if (checks + letters > 0)
    return `${plural(checks, 'check', 'checks')} · ${plural(letters, 'letter', 'letters')}`;
  const waiting = queue.steps.find((s) => s.id === 'waiting_for_claim')?.count ?? 0;
  if (waiting > 0) return `Nothing claimed · ${formatCount(waiting)} waiting for a claim`;
  const active = queue.views.find((view) => view.id === 'queue')?.count ?? queue.rows.length;
  if (active > 0)
    return `Nothing claimed · ${plural(active, 'active award', 'active awards')} in the queue`;
  return history === true
    ? 'Nothing claimed · all caught up'
    : history === false
      ? 'Nothing claimed · nothing has reached the queue yet'
      : 'Nothing claimed · nothing waiting';
}

/** "13 days since the letter", as SA1's board words the oldest wait past a deadline. */
export function sinceLetterWords(days: number): string {
  return days <= 0 ? 'Letter dated today' : `${plural(days, 'day', 'days')} since the letter`;
}

/**
 * SA1's three numbers, each a link to the list it counts (APP-044, as built 2026-10-20): My queue
 * to My queue, past deadline to the past-deadline elections (SA4), Shareworks rows to the
 * worklist (SA5), which counts them by the same rule. On My queue its own number is the page, so
 * it isn't a link there. `oldestLetterDays` comes from SA4's read: null while it loads, or when
 * no letter is dated, and the line says what can be done instead.
 */
export function queueHeroStats(
  queue: StockAdminQueue,
  view: QueueViewId,
  history: QueueHistory,
  oldestLetterDays: number | null,
): HeroStat[] {
  const mine = queue.mine.checks + queue.mine.letters;
  const late = queue.steps.find((s) => s.id === 'past_deadline')?.count ?? 0;
  const myQueue = {
    label: 'My queue · all sectors',
    value: formatCount(mine),
    meta: myQueueMeta(queue, history),
  } as const satisfies HeroStat;
  return [
    view === 'mine' ? myQueue : { ...myQueue, href: QUEUE_VIEW.mine.href },
    {
      label: 'All sectors · elections past deadline',
      value: formatCount(late),
      meta:
        late === 0
          ? 'None past deadline'
          : oldestLetterDays === null
            ? 'Chase, extend or close'
            : sinceLetterWords(oldestLetterDays),
      tone: late > 0 ? 'gold' : 'default',
      href: QUEUE_VIEW.past_deadline.href,
    },
    {
      // The worklist's own count of rows left (APP-067 §13), not the awards at this step.
      label: 'All sectors · Shareworks rows to enter',
      value: formatCount(queue.shareworksRows),
      meta: queue.shareworksRows === 0 ? 'No grants to enter' : 'Ready to enter',
      href: WORK_HREF.shareworks,
    },
  ];
}

/** The hero's sentence: what's waiting and who's on what, in plain words. */
export function queueSentence(
  queue: StockAdminQueue,
  meId: string,
  history: QueueHistory = null,
): string {
  const waiting = queue.steps.find((s) => s.id === 'waiting_for_claim')?.count ?? 0;
  const mine = queue.mine.checks + queue.mine.letters;
  const active = queue.views.find((view) => view.id === 'queue')?.count ?? queue.rows.length;
  const parts: string[] = [];
  if (active === 0 && waiting === 0 && mine === 0 && history === false)
    return 'Nothing has reached the queue yet. Submitted awards stay here from approval through Shareworks.';
  if (active === 0 && waiting === 0 && mine === 0)
    return 'Nothing is in progress right now. Submitted awards stay here from approval through Shareworks.';
  parts.push(
    waiting === 0
      ? 'The queue shows all active submitted awards with their current stage and claim.'
      : waiting === 1
        ? 'One approved award is waiting for a claim.'
        : `${formatCount(waiting)} approved awards are waiting for a claim.`,
  );
  if (mine > 0)
    parts.push(
      `You’re on ${plural(queue.mine.checks, 'check', 'checks')} and ${plural(queue.mine.letters, 'letter', 'letters')}.`,
    );
  const others = queue.rows.filter((r) => r.claimedBy !== null && r.claimedBy.id !== meId);
  const [first] = others;
  if (first?.claimedBy) parts.push(`${first.claimedBy.name} is on ${first.award.displayId}.`);
  return parts.join(' ');
}

export interface WorkingPerson {
  person: Person;
  you: boolean;
  /** Award numbers they hold, oldest first. */
  holds: string[];
}

/**
 * "Who's working the queue": you, then the rest of the team, each with what they hold. Read from
 * everything in progress (every claim is there).
 */
export function whoIsWorking(
  me: { id: string; name: string; initials: string },
  inProgress: StockAdminQueue | undefined,
  teammates: readonly Person[],
): WorkingPerson[] {
  const holds = (id: string): string[] =>
    (inProgress?.rows ?? []).filter((r) => r.claimedBy?.id === id).map((r) => r.award.displayId);
  return [
    { person: me, you: true, holds: holds(me.id) },
    ...teammates.map((t) => ({ person: t, you: false, holds: holds(t.id) })),
  ];
}

/** The empty state for a view with no rows. */
export function emptyView(
  view: QueueViewId,
  step: QueueStep | null,
  teammates: readonly Person[],
  history: QueueHistory = true,
): { headline: string; body: string; caughtUp: boolean } {
  const team =
    teammates.length === 0 ? 'you' : `you and ${teammates.map((t) => t.name).join(' and ')}`;
  const shown = emptyFor(view, step, team);
  // "Caught up" says the work was here and is done: only once some award has reached the team.
  if (!shown.caughtUp || history === true) return shown;
  if (view === 'queue')
    return history === false
      ? {
          headline: 'Nothing has reached the queue yet',
          body: `Submitted awards stay here from approval through Shareworks. We email ${team} when the first approved award arrives.`,
          caughtUp: false,
        }
      : {
          headline: 'Nothing in progress',
          body: `Submitted awards stay here through their active stages, and we email ${team} when an approved award arrives.`,
          caughtUp: false,
        };
  return { ...shown, caughtUp: false };
}

function emptyFor(
  view: QueueViewId,
  step: QueueStep | null,
  team: string,
): { headline: string; body: string; caughtUp: boolean } {
  // A step filter that matches nothing: its action shows everything in progress (APP-093).
  if (step !== null)
    return {
      headline: `Nothing at ${STEP[step].label.toLowerCase()}`,
      body: 'Awards show here when they reach this step.',
      caughtUp: false,
    };
  switch (view) {
    case 'queue':
      return {
        headline: 'All caught up',
        body: `There are no active submitted awards. Submitted awards stay here through their active stages, and we email ${team} when an approved award arrives.`,
        caughtUp: true,
      };
    case 'mine':
      return {
        headline: 'Nothing claimed',
        body: 'Claim an award in the queue and it stays here, with its check and its letters, until the letters go out.',
        caughtUp: false,
      };
    case 'tax':
      return {
        headline: 'No PAA tax to enter',
        body: 'Each PAA person’s taxes from ADP Quick Calc show on Tax to enter until the letters go out.',
        caughtUp: true,
      };
    case 'in_progress':
      return {
        headline: 'Nothing in progress',
        body: 'Every award from approval to Shareworks shows here, so you can help move any of them along.',
        caughtUp: false,
      };
    case 'past_deadline':
      return {
        headline: 'None past deadline',
        body: 'Elections that pass their deadline show here, so you can chase, extend or close them as cash.',
        caughtUp: true,
      };
    case 'letters':
      return {
        headline: 'No letters to prepare',
        body: 'When an award passes its check, it shows here. Its letter is made from the template, with every filled-in fact highlighted so you can read it before it goes out.',
        caughtUp: true,
      };
    case 'earning_statements':
      return {
        headline: 'No statements to check',
        body: 'When Payroll attaches an earning statement, the award shows here for you to check it against the estimate.',
        caughtUp: true,
      };
    case 'shareworks':
      return {
        headline: 'No grants to enter',
        body: 'Awards with options or shares show here when they’re ready to enter in Shareworks.',
        caughtUp: true,
      };
  }
}
